import { signal } from "@preact/signals-react";

import { clearAppData, clearAppDataCache } from "@/app/api/app-api";
import { setSessionRenewalHandler, type SessionRenewalOutcome } from "@/shared/api/api-client";
import { resetBedLocks } from "@/shared/api/bed-locks";
import { buildBackendUrl, hasConfiguredBackend } from "@/shared/api/backend";
import { clearCommentDrafts } from "@/pages/beds/utils/comment-drafts";
import { clearAuthUser, getAuthUser, getSessionExpiry, setAuthUser, type AuthUser } from "@/pages/authentication/utils/auth";
import { resetPresenceState } from "@/shared/api/presence";
import { stopSync } from "@/shared/api/sync";

export const sessionUser = signal<AuthUser | null>(getAuthUser());
export const sessionLoading = signal(false);
export const sessionReady = signal(!hasConfiguredBackend());

let sessionExpiryTimer: number | null = null;
let sessionIdleTimer: number | null = null;
let refreshInFlight: Promise<SessionRenewalOutcome> | null = null;
let lastActivityAt = 0;

const sessionIdleTimeoutMs = 10 * 60 * 1000;
const sessionActivityThrottleMs = 30 * 1000;

type MeResponse = {
    user?: {
        id?: string;
        name?: string;
        role?: string;
        handover_department_id?: string;
    };
};

export type SessionProbeOutcome = "valid" | "invalid" | "unavailable";

function userFromPayload(payload: MeResponse) {
	if (payload.user?.id && payload.user?.name && payload.user?.role) {
		return {
			id: payload.user.id,
			name: payload.user.name,
			role: payload.user.role,
			handover_department_id: payload.user.handover_department_id,
		} satisfies AuthUser;
	}

	return null;
}

function applySessionUser(user: AuthUser) {
    if (sessionUser.value?.id && sessionUser.value.id !== user.id) {
        clearCommentDrafts(sessionUser.value.id);
    }
    setAuthUser(user);
	if (sessionUser.value?.id !== user.id || sessionUser.value.name !== user.name || sessionUser.value.role !== user.role || sessionUser.value.handover_department_id !== user.handover_department_id) {
		sessionUser.value = user;
	}
	scheduleSessionExpiryLogout();
    scheduleSessionIdleLogout();
}

function applyPayload(payload: MeResponse) {
    const user = userFromPayload(payload);
    if (!user) {
        return false;
    }

    applySessionUser(user);
    return true;
}

function clearSessionExpiryTimer() {
	if (sessionExpiryTimer !== null) {
		window.clearTimeout(sessionExpiryTimer);
		sessionExpiryTimer = null;
	}
}

function clearSessionIdleTimer() {
	if (sessionIdleTimer !== null) {
		window.clearTimeout(sessionIdleTimer);
		sessionIdleTimer = null;
	}
}

function redirectToLogin() {
	redirectTo("/login");
}

function redirectTo(path: string) {
    if (window.location.pathname !== path) {
        window.location.assign(path);
    }
}

function isPublicRoute() {
    if (typeof window === "undefined") return false;
    const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
    const authenticatedExternalHandover = pathname === "/external/handover";
    return !authenticatedExternalHandover && (pathname === "/logout" || pathname === "/hap/entry" || pathname === "/status-report" || pathname === "/telefonist" || pathname === "/bed-board" || pathname === "/external" || pathname.startsWith("/external/"));
}


async function refreshSession(): Promise<SessionRenewalOutcome> {
	if (refreshInFlight) return refreshInFlight;
	refreshInFlight = refreshSessionOnce();
	try {
		return await refreshInFlight;
	} finally {
		refreshInFlight = null;
	}
}

async function refreshSessionOnce(): Promise<SessionRenewalOutcome> {
	if (!hasConfiguredBackend()) {
		return sessionUser.value
			? { kind: "renewed", user: sessionUser.value }
			: { kind: "invalid" };
	}

	let response: Response;
	try {
		response = await fetch(buildBackendUrl("/auth/refresh"), {
			method: "POST",
			credentials: "include",
		});
	} catch {
		return { kind: "unavailable", reason: "network failure" };
	}

	if (response.status === 401 || response.status === 403) {
		return { kind: "invalid" };
	}

	if (!response.ok) {
		return { kind: "unavailable", reason: `refresh failed with ${response.status}` };
	}

	try {
		const payload = (await response.json()) as MeResponse;
		if (!applyPayload(payload) || !sessionUser.value) {
			return { kind: "unavailable", reason: "malformed refresh response" };
		}
		return { kind: "renewed", user: sessionUser.value };
	} catch {
		return { kind: "unavailable", reason: "malformed refresh response" };
	}
}

export function expireSession() {
	clearSession();
	redirectToLogin();
}

function scheduleSessionExpiryLogout() {
	clearSessionExpiryTimer();
	const expiresAt = getSessionExpiry();
	if (expiresAt === null) {
		return;
	}

	const delay = expiresAt - Date.now();
	if (delay <= 0) {
		expireSession();
		return;
	}

	sessionExpiryTimer = window.setTimeout(() => {
		expireSession();
	}, delay);
}

function scheduleSessionIdleLogout() {
    clearSessionIdleTimer();
    if (!sessionUser.value) return;
    sessionIdleTimer = window.setTimeout(() => expireSession(), sessionIdleTimeoutMs);
}

function handleUserActivity(event?: Event) {
    const target = event?.target;
    if (target instanceof HTMLElement && target.closest("[data-skip-session-refresh]")) return;
    if (isPublicRoute() || !sessionUser.value || document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastActivityAt < sessionActivityThrottleMs) return;
    lastActivityAt = now;
    scheduleSessionIdleLogout();
    void refreshSession().then((result) => {
        if (result.kind === "invalid") expireSession();
    });
}

function initializeActivityTracking() {
    const events = ["pointerdown", "keydown", "touchstart", "focus"];
    for (const event of events) window.addEventListener(event, handleUserActivity, { passive: true });
    document.addEventListener("visibilitychange", handleUserActivity);
}

initializeActivityTracking();
setSessionRenewalHandler(() => refreshSession());

export async function fetchSession() {
    if (!hasConfiguredBackend()) {
        sessionUser.value = getAuthUser();
        sessionReady.value = true;
        return sessionUser.value;
    }

    sessionLoading.value = true;
    const probedUserID = sessionUser.value?.id ?? null;
    try {
        const result = await probeSession();
        if (result === "invalid" && sessionUser.value?.id === probedUserID) {
            clearSession();
        }
        return sessionUser.value;
    } finally {
        sessionLoading.value = false;
        sessionReady.value = true;
    }
}

export async function probeSession(): Promise<SessionProbeOutcome> {
    if (!hasConfiguredBackend()) {
        return sessionUser.value ? "valid" : "invalid";
    }

    let response: Response;
    try {
        response = await fetch(buildBackendUrl("/auth/me"), {
            method: "GET",
            credentials: "include",
        });
    } catch {
        return "unavailable";
    }

    if (response.ok) {
        try {
            const payload = (await response.json()) as MeResponse;
            return applyPayload(payload) ? "valid" : "unavailable";
        } catch {
            return "unavailable";
        }
    }

    if (response.status === 401) {
        const refreshed = await refreshSession();
        if (refreshed.kind === "renewed") return "valid";
        if (refreshed.kind === "invalid") return "invalid";
        return "unavailable";
    }

    return "unavailable";
}

export function applyAuthenticatedUser(user: AuthUser) {
    if (sessionUser.value?.id && sessionUser.value.id !== user.id) {
        clearCommentDrafts(sessionUser.value.id);
    }
    clearAppDataCache();
    resetPresenceState();
    setAuthUser(user);
    sessionUser.value = user;
    sessionReady.value = true;
	scheduleSessionExpiryLogout();
    scheduleSessionIdleLogout();
}

export function clearSession() {
    clearSessionExpiryTimer();
    clearSessionIdleTimer();
    clearCommentDrafts(sessionUser.value?.id ?? getAuthUser()?.id ?? "");
    clearAuthUser();
    clearAppData();
    clearAppDataCache();
    resetBedLocks();
    sessionUser.value = null;
    lastActivityAt = 0;
    stopSync();
    resetPresenceState();
    sessionReady.value = true;
}

async function requestBackendLogout() {
    if (!hasConfiguredBackend()) return;
    try {
        await fetch(buildBackendUrl("/auth/logout"), {
            method: "POST",
            credentials: "include",
        });
    } catch {
        // Local cleanup still runs when backend logout fails.
    }
}

export async function logoutSession(redirectPath = "/login") {
    await requestBackendLogout();
    clearSession();
    redirectTo(redirectPath);
}

function clearAccessibleCookies() {
    for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim();
        if (name) {
            document.cookie = `${name}=; Max-Age=0; path=/`;
        }
    }
}

async function clearIndexedDB() {
    if (!("databases" in indexedDB)) return;
    const databases = await indexedDB.databases();
    await Promise.all(databases.map((database) => database.name ? new Promise<void>((resolve) => {
        const request = indexedDB.deleteDatabase(database.name!);
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
        request.onblocked = () => resolve();
    }) : Promise.resolve()));
}

export async function clearSiteData() {
    await requestBackendLogout();
    clearSession();

    try {
        window.localStorage.clear();
    } catch {
        // Storage may be unavailable in restricted browser contexts.
    }
    try {
        window.sessionStorage.clear();
    } catch {
        // Storage may be unavailable in restricted browser contexts.
    }
    try {
        clearAccessibleCookies();
    } catch {
        // Cookie access may be unavailable in restricted browser contexts.
    }
    try {
        await clearIndexedDB();
    } catch {
        // IndexedDB may be unavailable or blocked by another open connection.
    }
    try {
        if ("caches" in window) {
            await Promise.all((await window.caches.keys()).map((key) => window.caches.delete(key)));
        }
    } catch {
        // Cache storage may be unavailable in restricted browser contexts.
    }
    try {
        if ("serviceWorker" in navigator) {
            await Promise.all((await navigator.serviceWorker.getRegistrations()).map((registration) => registration.unregister()));
        }
    } catch {
        // Service workers may be unavailable in restricted browser contexts.
    }

    redirectToLogin();
}
